'use client';

// `useNetworkMode()` — the SINGLE source of truth for which network the
// user is on. Every per-network decision in the app reads from here:
//
//   • SDK + tx-engine binding   (which backend / RPC URL)
//   • NetworkLink prefix                   (does href need /demo-mode/?)
//   • Banner mode                          (which banner to render)
//   • Auth re-establishment                (which backend to log into)
//
// ─── The rule (two lines) ──────────────────────────────────────────
//
//   1. If the `mainnet_enabled` Statsig gate is OFF
//        → return `'devnet'`. Pre-launch the user CANNOT reach mainnet,
//          regardless of what their preference cookie says.
//
//   2. Otherwise return the user's persisted preference from
//      `useNetworkStore` (cookie-backed, written by the wallet toggle).
//      Defaults to `'mainnet'` for new visitors post-launch.
//
// ─── Why this exists ───────────────────────────────────────────────
//
// The previous design read network from the URL pathname (`/demo-mode/*`
// → devnet, else mainnet). That broke on excepted routes like `/about`:
// a devnet user clicking "About" would land on a bare URL → SDK rebinds
// to mainnet → backend session was for devnet → user appears logged out.
//
// By making the mode store-driven (instead of URL-driven), excepted
// pages no longer disturb the SDK binding. The URL prefix becomes a
// cosmetic projection of mode (enforced by middleware redirects) rather
// than a source of mode.
//
// ─── Reactivity ────────────────────────────────────────────────────
//
// We subscribe to two stores so re-renders fire correctly:
//   • `useNetworkStore`        — for the wallet-toggle path
//   • `useDevToolkitStore`     — for the engineer-flips-gate path
//     (`isMainnetEnabled()` reads overrides synchronously but does not
//     subscribe; this companion subscription provides the re-render).

import type { Network } from './types';
import { useActiveNetwork } from './use-active-network';

/** The two USER-FACING networks. `'local'` (engineer-only protocol-level
 *  devnet variant with a localhost backend) is collapsed into `'devnet'`
 *  at the UX layer — banners, URL prefix, NetworkLink rewriting all
 *  treat them identically.
 *
 *  For INFRASTRUCTURE binding (SDK apiUrl, query cache, session cookie)
 *  use `useActiveNetwork()` instead — it preserves `'local'` because
 *  those callers do see a different backend origin. */
export type NetworkMode = Extract<Network, 'mainnet' | 'devnet'>;

export function useNetworkMode(): NetworkMode {
  const active = useActiveNetwork();
  return active === 'mainnet' ? 'mainnet' : 'devnet';
}
